import classNames from 'classnames'
import Form, {
    Item as FormItem,
    GroupItem,
    ColCountByScreen
} from 'devextreme-react/form';
import {
    TCMProject,
} from '../../types/tcm';
import { PatternRule, StringLengthRule } from 'devextreme-react/cjs/validator';
import { FormTextBox } from '../../components/FormTextBox/FormTextBox';
import { FormSelectBox } from '../../components/FormSelectBox/FormSelectBox';
import { FormDateBox } from '../../components/FormDateBox/FormDateBox';
import { useEffect, useState } from 'react';
import { fetchBuildingRecordById, fetchBuildingRecordsByRegion, fetchEstimateTypeRecords, fetchRegionNumbers, fetchTcmCostEstimateStatuses, fetchTcmPriceBooks } from '../TcmService';
import TcmCostEstimates from '../TcmCostEstimates/TcmCostEstimates';
import InfoHeader from '../components/InfoHeader';
import { computeProjectEquipmentTotal, computeProjectGrandTotal, computeProjectLaborTotal } from '../util/helperFunctions';
import { LoadingIndicator } from 'devextreme-react/cjs/bar-gauge';
import { useAuth } from '../../hooks/useAuth';

/** @typedef {import('../../types/tcm').TCMProject} TCMProject */

/**
 * @param {import('../../types/tcm').TCMProject} data
 * @param {boolean} editing
 * @param {function(field, value): void} updateField
 */
const TcmProjectFormDetails = ({ data, editing, updateField }) => {
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [buildingsByRegion, setBuildingsByRegion] = useState([]);
    const [loadingBuildingsByRegion, setLoadingBuildingsByRegion] = useState(false);
    const [loadingRegions, setLoadingRegions] = useState(false);
    const [loadingExistingBuilding, setLoadingExistingBuilding] = useState(false);
    const [estimateTypes, setEstimateTypes] = useState([]);
    const [tcmCostEstimateStatuses, setTcmCostEstimateStatuses] = useState([]);
    const [priceBooks, setPriceBooks] = useState([]);
    const { user } = useAuth();

    useEffect(() => {
        if (data.BuildingId > 0) {
            fetchExistingBuildingHelper();
        }
        fetchRegionNumbersHelper();
        helper_fetchEstimateTypeRecords();
        helper_fetchTcmCostEstimateStatuses();
        helper_fetchPriceBookRecords();
    }, []);
    useEffect(() => {
        if (selectedRegion !== undefined) {
            if (selectedRegion !== null) {
                if (selectedRegion > 0) {
                    fetchBuildingsByRegionNumberHelper();
                }
            }
        }
    }, [selectedRegion]);
    useEffect(() => {
        if (data.BuildingId !== undefined) {
            if (data.BuildingId !== null) {
                if (data.BuildingId > 0) {
                    fetchExistingBuildingHelper();
                }
            }
        }
    }, [data.BuildingId]);

    const helper_fetchPriceBookRecords = async () => {
        const result = await fetchTcmPriceBooks();
        setPriceBooks(result);
    }
    const helper_fetchEstimateTypeRecords = async () => {
        const result = await fetchEstimateTypeRecords();
        setEstimateTypes(result);
    }
    const helper_fetchTcmCostEstimateStatuses = async () => {
        const result = await fetchTcmCostEstimateStatuses();
        setTcmCostEstimateStatuses(result);
    }
    const fetchExistingBuildingHelper = async () => {
        setLoadingExistingBuilding(true);
        const buildingFetch = await fetchBuildingRecordById(data.BuildingId);
        setSelectedBuilding(buildingFetch);
        // Because we already have an existing region, set it for our selected region
        setSelectedRegion(buildingFetch.RegionNumber)
        setLoadingExistingBuilding(false);
    }
    const fetchRegionNumbersHelper = async () => {
        setLoadingRegions(true);
        const regionsFetch = await fetchRegionNumbers();
        setRegions(regionsFetch);
        setLoadingRegions(false);
    }
    const fetchBuildingsByRegionNumberHelper = async () => {
        setLoadingBuildingsByRegion(true);
        const regionBuildingsFetch = await fetchBuildingRecordsByRegion(selectedRegion);
        // console.log("checkRegion_BuildingData", regionBuildingsFetch)
        const sortedBuildings = [...regionBuildingsFetch].sort((a, b) =>
            a.SequenceNumber.localeCompare(b.SequenceNumber)
        );
        setBuildingsByRegion(sortedBuildings);
        setLoadingBuildingsByRegion(false);
    }
    function formatZipCode(zip) {
        if (!zip) return zip;

        // Already formatted: 12345-1234
        if (/^\d{5}-\d{4}$/.test(zip)) return zip;

        // 9 digits: 123451234 -> 12345-1234
        if (/^\d{9}$/.test(zip)) {
            return zip.slice(0, 5) + '-' + zip.slice(5);
        }

        // 5 digits: 12345
        if (/^\d{5}$/.test(zip)) return zip;

        // Otherwise, return as is
        return zip;
    }
    // Utility function to sanitize address values
    function sanitizeAddressValue(value) {
        const badValues = [undefined, null, '', 'null', 'undefined', 'N/A', 'none', 'NaN'];
        if (badValues.includes(value) || (typeof value === 'string' && value.trim() === '')) {
            return '';
        }
        return value;
    }

    const formatCurrency = (value) =>
        value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        });
    return (
        <Form className={classNames({ 'plain-styled-form': true, 'view-mode': !editing })}
            labelMode='floating'>
            <GroupItem colCount={4} caption='General Information' >
                <ColCountByScreen xs={4} />
                <FormItem>
                    <FormTextBox
                        label='Project Description'
                        value={data.ProjectDescription}
                        isEditing={editing}
                        onValueChange={e => updateField('ProjectDescription', e)}
                        maxLength={50}
                        required={true}
                    >
                        <PatternRule
                            pattern="^[a-zA-Z0-9 ]+$"
                            message="No special characters are allowed."
                        />
                    </FormTextBox>
                </FormItem>
                <FormItem>
                    <FormDateBox
                        label='Project Start Date'
                        value={data.ProjectStartDate}
                        isEditing={editing}
                        onValueChange={e => updateField('ProjectStartDate', e)}
                        required={true}
                    />

                </FormItem>
                <FormItem>
                    <FormDateBox
                        label='Project End Date'
                        value={data.ProjectEndDate}
                        isEditing={editing}
                        onValueChange={e => updateField('ProjectEndDate', e)}
                        required={true}
                    />
                </FormItem>

                <FormItem>
                    <FormTextBox
                        label='Comments'
                        value={data.Comments}
                        isEditing={editing}
                        onValueChange={e => updateField('Comments', e)}
                        maxLength={100}
                    >
                        <PatternRule
                            pattern="^[a-zA-Z0-9 ]+$"
                            message="No special characters are allowed."
                        />
                    </FormTextBox>
                </FormItem>
            </GroupItem>
            <GroupItem colCount={4} caption='Building Information' >
                <ColCountByScreen xs={4} />
                <FormItem>
                    <FormSelectBox
                        label='Region Number'
                        value={selectedRegion}
                        isEditing={editing}
                        dataSource={loadingRegions ? [] : regions}
                        readOnly={loadingRegions}
                        displayExpr={"RegionNumber"}
                        valueExpr={"RegionNumber"}
                        onValueChange={e => setSelectedRegion(e)}
                        required={true}
                        searchEnabled={true}
                        noDataText="No regions to display"
                    />
                </FormItem>
                {/* {loadingRegions && <FormItem><LoadingIndicator show={true} text='Loading Regions...'/></FormItem>} */}
                <FormItem dataField='BuildingId'>
                    <FormSelectBox
                        label='Building Number'
                        value={data.BuildingId}
                        isEditing={editing}
                        dataSource={loadingBuildingsByRegion ? [] : buildingsByRegion}
                        readOnly={loadingBuildingsByRegion || !selectedRegion}
                        displayExpr={"SequenceNumber"}
                        valueExpr={"BuildingId"}
                        onValueChange={e => updateField('BuildingId', e)}
                        required={true}
                        searchEnabled={true}
                        noDataText="No buildings to display"
                    />
                </FormItem>
                <FormItem>
                    <FormTextBox
                        label='Address 1'
                        value={sanitizeAddressValue(selectedBuilding?.Location?.Address1)}
                        readOnly={true}
                    />
                </FormItem>
                <FormItem>
                    <FormTextBox
                        label='Address 2'
                        value={sanitizeAddressValue(selectedBuilding?.Location?.Address2)}
                        readOnly={true}
                    />
                </FormItem>
                <FormItem>
                    <FormTextBox
                        label='City'
                        value={sanitizeAddressValue(selectedBuilding?.Location?.CityName)}
                        readOnly={true}
                    />
                </FormItem>
                <FormItem>
                    <FormTextBox
                        label='State'
                        value={sanitizeAddressValue(selectedBuilding?.Location?.State)}
                        readOnly={true}
                    />
                </FormItem>
                <FormItem>
                    <FormTextBox
                        label='Zip Code'
                        value={sanitizeAddressValue(formatZipCode(selectedBuilding?.Location?.ZipCode))}
                        readOnly={true}
                    />
                </FormItem>
            </GroupItem>
            <GroupItem caption='TCM Cost Estimates'>
                <FormItem>
                    <TcmCostEstimates
                        data={data}
                        editing={editing}
                        updateField={updateField}
                        estimateTypes={estimateTypes}
                        statuses={tcmCostEstimateStatuses}
                        priceBooks={priceBooks}
                        user={user}
                        showWorkflowActions={true}
                    />
                </FormItem>
            </GroupItem>
            <GroupItem caption='Project Totals' colCount={4}>
                {/* Project Labor Total (display-only) */}
                <FormItem
                    labelMode="static"
                    elementAttr={{ class: 'form-editor' }}
                    render={() => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <InfoHeader
                                    label="Project Labor Total"
                                    tooltip="Project Labor Total = Sum of (Labor Rate * Labor Hours) for all line items."
                                />
                            </div>
                            <FormTextBox
                                value={formatCurrency(computeProjectLaborTotal(data?.TCMCostEstimates))}
                                isEditing={false}
                                onValueChange={() => { }}
                            />
                        </div>
                    )}
                />
                {/* Project Equipment Total (display-only) */}
                <FormItem
                    labelMode="static"
                    elementAttr={{ class: 'form-editor' }}
                    render={() => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <InfoHeader
                                    label="Project Equipment Total"
                                    tooltip="Project Equipment Total = Sum of (Unit Price * Quantity) for all line items."
                                />
                            </div>
                            <FormTextBox
                                value={formatCurrency(computeProjectEquipmentTotal(data?.TCMCostEstimates))}
                                isEditing={false}
                                onValueChange={() => { }}
                            />
                        </div>
                    )}
                />
                {/* Project Grand Total (display-only) */}
                <FormItem
                    labelMode="static"
                    elementAttr={{ class: 'form-editor' }}
                    render={() => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <InfoHeader
                                    label={'Project Total Cost'}
                                    tooltip={"Project Total Cost = (Equipment Total + Labor Total) * (1 + O&M Rate)"}
                                />
                            </div>
                            <FormTextBox
                                value={formatCurrency(computeProjectGrandTotal(data))}
                                isEditing={false}
                                onValueChange={() => { }}
                            />
                        </div>
                    )}
                />
            </GroupItem>
        </Form>
    );
};
export default TcmProjectFormDetails;